import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../WithdrawPage/withdrawal.css";
import "./security.css";
import "./closeAccount.css";

const STEP_RETENTION = 1;
const STEP_REASON = 2;
const STEP_ASSETS = 3;

const REASON_NO_LONGER = "no_longer";
const REASON_MERGE = "merge";
const REASON_OTHERS = "others";

function clearAuthTokensAndNotify() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userId");
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event("agce-auth-changed"));
  window.dispatchEvent(new Event("agce-auth-token-changed"));
}

/** Backend expects `type: "phone"` for SMS; UI state uses `"mobile"`. */
function toClosedAccountSubmitType(uiVerifyModal) {
  if (uiVerifyModal === "mobile") return "phone";
  return uiVerifyModal;
}

/** Match send-otp payload (`phone` for SMS when aligned with closed-account). */
function toClosedAccountSendOtpChannel(uiChannel) {
  return uiChannel === "mobile" ? "phone" : "email";
}

const METHOD_PRIORITY = ["passkey", "totp", "email", "mobile"];

const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

function normalizeSecurityMethods(result) {
  const raw =
    result?.data?.security_methods ||
    result?.data?.data?.security_methods ||
    result?.security_methods ||
    result?.data?.securityMethods ||
    null;
  if (!raw || typeof raw !== "object") return null;
  return {
    email: !!raw.email,
    mobile: !!(raw.mobile ?? raw.phone ?? raw.sms),
    totp: !!raw.totp,
    passkey: !!raw.passkey,
  };
}

/** Display `estimated_total_btc` from wallet-total-btc API */
function formatEstimatedTotalBtc(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 8 }).format(n);
}

function pickPreferredVerificationMethod(methods) {
  if (!methods) return null;
  const hit = METHOD_PRIORITY.find((k) => methods[k]);
  return hit || null;
}

function ChooseVerificationMethodModal({ methods, onClose, onPick, onSecurityUnavailable }) {
  return (
    <div className="withdraw_confirm_overlay pm-choose-method-overlay-stacked" role="presentation">
      <div className="withdraw_choose_modal pm-choose-method-modal" role="dialog" aria-modal="true" aria-labelledby="ca-choose-verify-title">
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="ca-choose-verify-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button key={key} type="button" className="withdraw_choose_item pm-choose-method-item" onClick={() => onPick(key)}>
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const CONFIRM_POINTS = [
  "If you create a new account after closing this one, your identity verification may be restricted for 30 days or longer.",
  "Please note that your transaction records will no longer be accessible once your account is closed. We recommend downloading them before proceeding with account deletion. Refer to the FAQ for download instructions.",
  "If you close your account and register a new one under a different referral relationship, you may lose eligibility for any referral rewards or rebates.",
];

/**
 * Close Account — multi-step flow + modals.
 * Route: /user_profile/security/closeAccount
 */
const CloseAccount = () => {
  const navigate = useNavigate();
  const { userDetails } = useContext(ProfileContext);

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");

  const maskedEmail = useMemo(() => maskEmailForDisplay(accountEmail), [accountEmail]);
  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  const [step, setStep] = useState(STEP_RETENTION);
  const [reason, setReason] = useState(REASON_NO_LONGER);
  const [assetWaiver, setAssetWaiver] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecks, setConfirmChecks] = useState([false, false, false]);

  /** Normalized flags from GET `/v1/security/get-security-methods-list` (loaded once on mount). */
  const [resolvedMethods, setResolvedMethods] = useState(null);
  const [methodsPrefetchLoading, setMethodsPrefetchLoading] = useState(true);
  const [methodsPrefetchError, setMethodsPrefetchError] = useState("");

  /** null | "passkey" | "totp" | "email" | "mobile" */
  const [verifyModal, setVerifyModal] = useState(null);
  const [chooseMethodsOpen, setChooseMethodsOpen] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  /** Only after a failed/cancelled passkey attempt — avoids stacking UI while WebAuthn is active. */
  const [passkeyAlternateEligible, setPasskeyAlternateEligible] = useState(false);

  /** From GET `/v1/security/wallet-total-btc` (`data.estimated_total_btc`). */
  const [estimatedTotalBtc, setEstimatedTotalBtc] = useState(null);
  const [walletTotalLoading, setWalletTotalLoading] = useState(true);

  const prefetchSecurityMethods = useCallback(async () => {
    setMethodsPrefetchLoading(true);
    setMethodsPrefetchError("");
    try {
      const result = await AuthService.getSecurityMethodsList();
      const m = normalizeSecurityMethods(result);
      if (!result?.success || !m) {
        setResolvedMethods(null);
        setMethodsPrefetchError(result?.message || "Could not load security methods.");
        return;
      }
      setResolvedMethods(m);
    } catch (e) {
      setResolvedMethods(null);
      setMethodsPrefetchError(e?.message || "Network error. Please try again.");
    } finally {
      setMethodsPrefetchLoading(false);
    }
  }, []);

  useEffect(() => {
    void prefetchSecurityMethods();
  }, [prefetchSecurityMethods]);

  const fetchWalletTotalBtc = useCallback(async () => {
    setWalletTotalLoading(true);
    try {
      const result = await AuthService.getSecurityWalletTotalBtc();
      const raw =
        result?.data?.estimated_total_btc ??
        result?.data?.data?.estimated_total_btc ??
        result?.estimated_total_btc;
      if (result?.success && raw != null && raw !== "") {
        const n = Number(raw);
        setEstimatedTotalBtc(Number.isFinite(n) ? n : null);
      } else {
        setEstimatedTotalBtc(null);
      }
    } catch {
      setEstimatedTotalBtc(null);
    } finally {
      setWalletTotalLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchWalletTotalBtc();
  }, [fetchWalletTotalBtc]);

  const goSecurity = () => navigate("/user_profile/security", { replace: true });
  const goSecurityUnavailable = () => navigate("/asset_managemnet/security_verification_unavailable");

  const handleCloseSuccess = useCallback(
    (message) => {
      const msg =
        typeof message === "string" && message.trim()
          ? message.trim()
          : "Your account has been closed.";
      alertSuccessMessage(msg);
      clearAuthTokensAndNotify();
      navigate("/login", { replace: true });
    },
    [navigate]
  );

  const hasVerificationMethod = useMemo(
    () => resolvedMethods != null && pickPreferredVerificationMethod(resolvedMethods) != null,
    [resolvedMethods]
  );

  const resetVerificationInputs = useCallback(() => {
    setOtpCode("");
    setTotpCode("");
  }, []);

  const closeVerifyFlow = useCallback(() => {
    setVerifyModal(null);
    setChooseMethodsOpen(false);
    setPasskeyAlternateEligible(false);
    resetVerificationInputs();
  }, [resetVerificationInputs]);

  /** @returns {Promise<"success"|"cancelled"|"failed"|"invalid">} */
  const verifyPasskey = useCallback(
    async (opts = {}) => {
      const { silentCancel = false } = opts;
      const signId = accountEmail || accountPhoneDigits;
      if (!signId) {
        alertErrorMessage("Account identifier missing for passkey verification.");
        return "invalid";
      }
      setPasskeyAlternateEligible(false);
      setPasskeyBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        const optionsResult = await AuthService.passkeyGetAuthOptions(signId);
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        let credential;
        LoaderHelper.loaderStatus(false);
        try {
          credential = await startAuthentication(optionsResult.data);
        } catch (webauthnError) {
          LoaderHelper.loaderStatus(false);
          if (webauthnError?.name === "NotAllowedError") {
            if (!silentCancel) {
              alertErrorMessage("Authentication was cancelled or timed out. Try again or use another method.");
            }
            setPasskeyAlternateEligible(true);
            return "cancelled";
          }
          alertErrorMessage("Passkey verification failed.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        LoaderHelper.loaderStatus(true);
        const verifyResult = await AuthService.securityClosedAccount({
          type: "passkey",
          code: "",
          credential,
        });
        if (verifyResult?.success) {
          handleCloseSuccess(verifyResult?.message);
          return "success";
        }
        alertErrorMessage(verifyResult?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } catch (e) {
        alertErrorMessage(e?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } finally {
        setPasskeyBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [accountEmail, accountPhoneDigits, handleCloseSuccess]
  );

  const alternateMethodKeys = useMemo(() => {
    if (!resolvedMethods || !verifyModal) return [];
    return METHOD_PRIORITY.filter((k) => resolvedMethods[k] && k !== verifyModal);
  }, [resolvedMethods, verifyModal]);

  const sendCloseAccountOtp = useCallback(async (target) => {
    const security_methods = toClosedAccountSendOtpChannel(target);
    setOtpSending(true);
    try {
      const result = await AuthService.securityClosedAccountSendOtp(security_methods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, []);

  const openChooseMethods = useCallback(() => {
    if (alternateMethodKeys.length === 0) {
      alertErrorMessage("No other verification method is available.");
      return;
    }
    setChooseMethodsOpen(true);
  }, [alternateMethodKeys.length]);

  const applyVerificationMethod = useCallback(
    async (method) => {
      setChooseMethodsOpen(false);
      resetVerificationInputs();
      if (method === "passkey") {
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }
      setVerifyModal(method);
    },
    [resetVerificationInputs, verifyPasskey, closeVerifyFlow]
  );

  const startVerificationAfterConfirm = async () => {
    if (flowBusy || passkeyBusy || methodsPrefetchLoading) return;
    if (methodsPrefetchError && !resolvedMethods) {
      alertErrorMessage(methodsPrefetchError);
      return;
    }
    if (!hasVerificationMethod) {
      alertErrorMessage("Please add a verification method first");
      return;
    }
    const preferred = pickPreferredVerificationMethod(resolvedMethods);
    if (!preferred) return;

    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      resetVerificationInputs();

      if (preferred === "passkey") {
        setChooseMethodsOpen(false);
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }

      setVerifyModal(preferred);
    } catch (e) {
      alertErrorMessage(e?.message || "Something went wrong.");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const allConfirmChecked = confirmChecks.every(Boolean);

  const openConfirm = () => {
    if (!assetWaiver) return;
    setConfirmOpen(true);
  };

  const handleConfirmModal = () => {
    if (!allConfirmChecked) return;
    if (methodsPrefetchLoading) {
      alertErrorMessage("Still loading security methods. Please wait.");
      return;
    }
    if (methodsPrefetchError && !resolvedMethods) {
      alertErrorMessage(methodsPrefetchError);
      return;
    }
    if (!hasVerificationMethod) {
      alertErrorMessage("Please add a verification method first.");
      return;
    }
    setConfirmOpen(false);
    setConfirmChecks([false, false, false]);
    void startVerificationAfterConfirm();
  };

  const verifyTotpSubmit = async () => {
    const code = String(totpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return;
    }
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.securityClosedAccount({
        type: "totp",
        code,
        credential: "",
      });
      if (res?.success) {
        handleCloseSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Verification failed");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyEmailOrMobileSubmit = async () => {
    const code = String(otpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) return;
    const type = toClosedAccountSubmitType(verifyModal);
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.securityClosedAccount({
        type,
        code,
        credential: "",
      });
      if (res?.success) {
        handleCloseSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Invalid OTP");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePasteTotp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) setTotpCode(digits);
    } catch {
      // ignore
    }
  };

  const toggleConfirmCheck = (idx) => {
    setConfirmChecks((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const showChooser = chooseMethodsOpen && verifyModal;

  const assetsContinueDisabled =
    !assetWaiver || methodsPrefetchLoading || Boolean(methodsPrefetchError) || !hasVerificationMethod;

  return (
    <main className="ca-page" aria-labelledby="ca-title" data-ca-security-methods-loaded={resolvedMethods ? "true" : "false"}>
      <nav className="ca-page__crumbs" aria-label="Breadcrumb">
        <ol className="ca-page__crumbList">
          <li className="ca-page__crumbItem">
            <button type="button" className="ca-page__crumbLink" onClick={goSecurity}>
              Security
            </button>
          </li>
          <li className="ca-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="ca-page__crumbItem ca-page__crumbItem--active" aria-current="page">
            Close Account
          </li>
        </ol>
      </nav>

      <div className="security_section_bl">
        <h1 id="ca-title" className="ca-page__title">
          Close Account
        </h1>

        {methodsPrefetchLoading ? (
          <p className="ca-prefetchHint" role="status">
            Checking your security methods…
          </p>
        ) : null}

        {!methodsPrefetchLoading && methodsPrefetchError ? (
          <div className="ca-errorBanner ca-errorBanner--page" role="alert">
            <p className="ca-errorBanner__text">{methodsPrefetchError}</p>
            <button type="button" className="ca-btnGhost ca-btnGhost--inline" onClick={() => void prefetchSecurityMethods()}>
              Retry
            </button>
          </div>
        ) : null}

        {!methodsPrefetchLoading && resolvedMethods && !hasVerificationMethod ? (
          <div className="ca-errorBanner ca-errorBanner--page" role="alert">
            <p className="ca-errorBanner__text">Please add a verification method in Security settings before closing your account.</p>
            <button type="button" className="ca-btnGhost ca-btnGhost--inline" onClick={() => goSecurity()}>
              Back to Security
            </button>
          </div>
        ) : null}

        {step === STEP_RETENTION ? (
          <section className="ca-retention" aria-labelledby="ca-retention-heading">
            <h2 id="ca-retention-heading" className="ca-retention__sub">
              Personal Data Retention
            </h2>
            <p className="ca-retention__text">
              We inform you that the personal data you provided while using our services will be retained as long as necessary to
              fulfill the purposes outlined in our Privacy Policy, and to comply with legal obligations such as tax, accounting,
              Anti-Money Laundering regulations, or to resolve disputes and legal claims.
            </p>
            <button type="button" className="ca-btn-primary" onClick={() => setStep(STEP_REASON)}>
              Disable Account
            </button>
          </section>
        ) : null}

        {step === STEP_REASON ? (
          <section className="ca-reason" aria-labelledby="ca-reason-heading">
            <h2 id="ca-reason-heading" className="ca-reason__heading">
              Reason for Closure
            </h2>
            <div className="ca-reason__list" role="radiogroup" aria-label="Reason for closure">
              {[
                { id: REASON_NO_LONGER, label: "I no longer wish to use this account" },
                { id: REASON_MERGE, label: "Merge multiple accounts into one" },
                { id: REASON_OTHERS, label: "Others" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={reason === opt.id}
                  className={`ca-reason__option ${reason === opt.id ? "is-selected" : ""}`}
                  onClick={() => setReason(opt.id)}
                >
                  <span>{opt.label}</span>
                  <span className="ca-reason__radio" aria-hidden="true" />
                </button>
              ))}
            </div>
            <button type="button" className="ca-btn-primary" onClick={() => setStep(STEP_ASSETS)}>
              Continue
            </button>
          </section>
        ) : null}

        {step === STEP_ASSETS ? (
          <section className="ca-assets" aria-labelledby="ca-assets-heading">
            <p id="ca-assets-heading" className="ca-assets__lead">
              You have the following assets:
            </p>
            <div className="ca-assets__list">
              <div className="ca-assets__row">
                <span>NFT: 0</span>
                <span className="ca-assets__radioArt" aria-hidden="true" />
              </div>
              <div className="ca-assets__row">
                <span>
                  Assets:{walletTotalLoading ? " …" : ` ${formatEstimatedTotalBtc(estimatedTotalBtc)}`} BTC
                </span>
                <span className="ca-assets__radioArt" aria-hidden="true" />
              </div>
            </div>

            <label className="ca-assets__waiver">
              <input type="checkbox" checked={assetWaiver} onChange={(e) => setAssetWaiver(e.target.checked)} />
              <span>
                I agree to forfeit any remaining assets in my account and confirm that I fully waive and release all claims, and will
                not make any claims, against AGCX in connection with the closure of my account or the forfeiture of my account balance.
              </span>
            </label>

            <button type="button" className="ca-btn-primary" disabled={assetsContinueDisabled} onClick={openConfirm}>
              Close Account
            </button>
          </section>
        ) : null}

        {confirmOpen ? (
          <div className="ca-modal-overlay" role="presentation">
            <div
              className="ca-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ca-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="ca-modal__close" aria-label="Close" onClick={() => setConfirmOpen(false)}>
                ×
              </button>
              <h2 id="ca-modal-title" className="ca-modal__title">
                Are you sure you want to close your account?
              </h2>
              <p className="ca-modal__subtitle">Please read through the information below and ensure all three checkboxes are ticked.</p>

              <div className="ca-modal__box">
                {CONFIRM_POINTS.map((text, idx) => (
                  <label key={text} className="ca-modal__check">
                    <input type="checkbox" checked={confirmChecks[idx]} onChange={() => toggleConfirmCheck(idx)} />
                    <span>{text}</span>
                  </label>
                ))}
              </div>

              <button
                type="button"
                className="ca-modal__confirm"
                disabled={!allConfirmChecked || methodsPrefetchLoading || !hasVerificationMethod}
                onClick={() => void handleConfirmModal()}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : null}

        {verifyModal === "passkey" && !showChooser ? (
          <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation">
            <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
              <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
                ×
              </button>
              <div className="withdraw_passkey_title">Verify with passkey</div>
              <div className="withdraw_passkey_hero" aria-hidden="true">
                <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
                <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
              </div>
              <div className="withdraw_passkey_text">Complete verification using your passkey</div>
              <div className="withdraw_passkey_subtext">
                Please follow the instructions on your browser to complete verification.
              </div>
              <button type="button" className="withdraw_passkey_verify" disabled={passkeyBusy} onClick={() => void verifyPasskey({ silentCancel: false })}>
                {passkeyBusy ? "Verifying…" : "Verify"}
              </button>
              {alternateMethodKeys.length > 0 && passkeyAlternateEligible ? (
                <button type="button" className="withdraw_passkey_link" onClick={openChooseMethods}>
                  Use another verification method
                </button>
              ) : null}
              <button type="button" className="withdraw_passkey_link" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
              <div className="withdraw_passkey_footer">
                <img src="/images/protected_icon.svg" alt="shield icon" />
                <span>Protected by Balance Risk</span>
              </div>
            </div>
          </div>
        ) : null}

        {verifyModal === "totp" && !showChooser ? (
          <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
            <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
              <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
                ×
              </button>
              <div className="withdraw_auth_title">Authenticator App Verification</div>
              <div className="withdraw_auth_subtitle">
                Enter the 6-digit code generated by the
                <br />
                Authenticator App.
              </div>
              <div className="withdraw_auth_label">Authenticator App</div>
              <div className="withdraw_auth_input_row">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder=""
                  value={totpCode}
                  onChange={(e) => setTotpCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
                />
                <button type="button" className="withdraw_auth_paste" onClick={() => void handlePasteTotp()}>
                  Paste
                </button>
              </div>
              <button type="button" className="withdraw_auth_submit" disabled={flowBusy} onClick={() => void verifyTotpSubmit()}>
                {flowBusy ? "Verifying…" : "Submit"}
              </button>
              <div className="gapbtn_space">
                {alternateMethodKeys.length > 0 ? (
                  <button type="button" className="withdraw_auth_link" onClick={openChooseMethods}>
                    Switch to Another Verification Method
                  </button>
                ) : null}
                <button type="button" className="withdraw_auth_link" onClick={goSecurityUnavailable}>
                  Security verification unavailable?
                </button>
              </div>
              <div className="withdraw_passkey_footer withdraw_auth_footer">
                <img src="/images/protected_icon.svg" alt="shield icon" />
                <span>Protected by Balance Risk</span>
              </div>
            </div>
          </div>
        ) : null}

        {(verifyModal === "email" || verifyModal === "mobile") && !showChooser ? (
          <div className="tf-sec-page__daev-overlay" role="presentation">
            <div
              className="tf-sec-page__daev-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ca-daev-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="tf-sec-page__daev-close" aria-label="Close" onClick={closeVerifyFlow}>
                ×
              </button>

              <h2 id="ca-daev-title" className="tf-sec-page__daev-title">
                {verifyModal === "mobile" ? "Mobile Verification" : "Email Verification"}
              </h2>
              <p className="tf-sec-page__daev-desc">
                {verifyModal === "mobile"
                  ? `Tap Get Code to receive a 6-digit OTP at ${maskedPhone}, then enter it below.`
                  : `Tap Get Code to receive a 6-digit OTP at ${maskedEmail}, then enter it below.`}
              </p>

              <label className="tf-sec-page__daev-label" htmlFor="ca-daev-code">
                Enter Verification Code
              </label>
              <div className="tf-sec-page__daev-inputWrap">
                <input
                  id="ca-daev-code"
                  className="tf-sec-page__daev-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder=""
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
                <button type="button" className="tf-sec-page__daev-getCode" disabled={otpSending} onClick={() => void sendCloseAccountOtp(verifyModal)}>
                  {otpSending ? "Sending…" : "Get Code"}
                </button>
              </div>

              <button
                type="button"
                className="tf-sec-page__daev-submit"
                disabled={otpCode.length !== 6 || flowBusy}
                onClick={() => void verifyEmailOrMobileSubmit()}
              >
                {flowBusy ? "Verifying…" : "Submit"}
              </button>

              <div className="gapbtn_space">
                {alternateMethodKeys.length > 0 ? (
                  <button type="button" className="tf-sec-page__daev-help" onClick={openChooseMethods}>
                    Switch verification method
                  </button>
                ) : null}
                <button type="button" className="tf-sec-page__daev-help" onClick={goSecurityUnavailable}>
                  Security verification unavailable?
                </button>
              </div>

              <div className="tf-sec-page__daev-footer">
                <img src="/images/protected_icon.svg" alt="shield icon" />
                <span>Protected by Balance Risk</span>
              </div>
            </div>
          </div>
        ) : null}

        {showChooser ? (
          <ChooseVerificationMethodModal
            methods={alternateMethodKeys}
            onClose={() => setChooseMethodsOpen(false)}
            onPick={(key) => void applyVerificationMethod(key)}
            onSecurityUnavailable={goSecurityUnavailable}
          />
        ) : null}
      </div>
    </main>
  );
};

export default CloseAccount;
